"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { JsonLdScript } from "@/components/seo/json-ld-script";
import { cn } from "@/lib/utils";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  /** Index of the item expanded by default. Pass -1 to start fully collapsed. */
  defaultOpenIndex?: number;
}

export function FAQSection({ faqs, defaultOpenIndex = 0 }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(
    defaultOpenIndex >= 0 ? defaultOpenIndex : null
  );

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <JsonLdScript data={faqSchema} />
      <div>
        <div className="space-y-2">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={cn(
                  "overflow-hidden rounded-2xl border bg-[#0D1014] transition-all duration-300",
                  isOpen
                    ? "border-white/15 bg-white/[0.045]"
                    : "border-white/[0.07] hover:border-white/10"
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left md:p-6"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.05] text-xs font-semibold text-white/35">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-base font-semibold text-white md:text-lg">
                      {faq.question}
                    </h3>
                  </div>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04]">
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-white/40 transition-transform duration-300",
                        isOpen && "rotate-180 text-white/80"
                      )}
                    />
                  </span>
                </button>
                <div
                  className={cn(
                    "grid transition-all duration-300 ease-in-out",
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-6 pl-[4.75rem] leading-relaxed text-white/55 md:px-6 md:pl-[5.25rem]">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
